import type { AssetType } from '@/types/asset';

// Page types that get a location-specific premium heading; anything else falls
// back to the generic `other` copy.
type LocalisedPageType = 'category' | 'collection';

// Tolerates any string so callers can pass raw `getPageType()` output without
// narrowing.
export type SectionPageType = LocalisedPageType | (string & {});

type HeadingBucket = LocalisedPageType | 'other';

export type SectionAssetType = AssetType | 'lut';

export interface SectionCopy {
  premiumHeading: string;
}

const sectionHeading: Record<
  HeadingBucket,
  Record<SectionAssetType, string>
> = {
  category: {
    music: 'Premium tracks in this category',
    sfx: 'Premium SFX in this category',
    motion: 'Premium motion assets',
    stock_footage: 'Premium stock video clips',
    lut: 'Premium LUTs',
  },
  collection: {
    music: 'Premium tracks in this collection',
    sfx: 'Premium SFX in this collection',
    motion: 'Premium motion assets',
    stock_footage: 'Premium stock video clips',
    lut: 'Premium LUTs',
  },
  other: {
    music: 'Premium tracks',
    sfx: 'Premium sound effects',
    motion: 'Premium motion assets',
    stock_footage: 'Premium stock video clips',
    lut: 'Premium LUTs',
  },
};

// Noun used when addressing a tier's assets in body copy (e.g. empty states).
const assetNoun: Record<SectionAssetType, string> = {
  music: 'tracks',
  sfx: 'sound effects',
  motion: 'motion assets',
  stock_footage: 'videos',
  lut: 'LUTs',
};

export interface FreeSectionEmptyCopy {
  subtitle: string;
  title: string;
}

// Copy for the "free tier empty but premium has matches" state. Varies only by
// asset type (the noun).
export function getFreeSectionEmptyCopy(
  assetType: SectionAssetType
): FreeSectionEmptyCopy {
  const noun = assetNoun[assetType];
  return {
    title: `No free ${noun} match your search`,
    subtitle: `Take a look at the premium ${noun} below`,
  };
}

const isLocalisedPageType = (
  pageType: SectionPageType
): pageType is LocalisedPageType =>
  pageType === 'category' || pageType === 'collection';

export function getSectionCopy({
  assetType,
  pageType,
}: {
  assetType: SectionAssetType;
  pageType: SectionPageType;
}): SectionCopy {
  const bucket: HeadingBucket = isLocalisedPageType(pageType)
    ? pageType
    : 'other';
  return { premiumHeading: sectionHeading[bucket][assetType] };
}
